import React, { useEffect, useRef, useState } from "react";
import { PenTool, Type, Eraser } from "lucide-react";

/**
 * Digital signature pad with two modes:
 *  - Draw: HTML5 canvas with mouse / touch
 *  - Type: type your name; rendered in a cursive script font on a hidden canvas
 *
 * Always emits a base64 PNG data URL via onChange so PDF/print logic stays unchanged.
 */
export default function SignaturePad({ value, onChange, testId }) {
  const [mode, setMode] = useState("draw");
  const [typedName, setTypedName] = useState("");
  const canvasRef = useRef(null);
  const typeCanvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(!!value);

  // ---- Draw mode ----
  useEffect(() => {
    if (mode !== "draw") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0a0a0a";
    if (value && !typedName) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = value;
    }
  }, [mode]); // eslint-disable-line

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };
  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    const data = canvasRef.current.toDataURL("image/png");
    onChange?.(data);
  };

  // ---- Type mode ----
  const renderTypedSignature = (name) => {
    setTypedName(name);
    if (!name) {
      onChange?.(null);
      setHasInk(false);
      return;
    }
    // Render to off-screen canvas
    const canvas = typeCanvasRef.current || document.createElement("canvas");
    typeCanvasRef.current = canvas;
    const w = 600;
    const h = 180;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "#0a0a0a";
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    // Scale font size to fit
    let fontSize = 64;
    ctx.font = `italic ${fontSize}px "Brush Script MT", "Lucida Handwriting", cursive`;
    while (ctx.measureText(name).width > w - 40 && fontSize > 24) {
      fontSize -= 2;
      ctx.font = `italic ${fontSize}px "Brush Script MT", "Lucida Handwriting", cursive`;
    }
    ctx.fillText(name, 20, h / 2);
    const data = canvas.toDataURL("image/png");
    onChange?.(data);
    setHasInk(true);
  };

  const clear = () => {
    if (mode === "draw") {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    } else {
      setTypedName("");
    }
    setHasInk(false);
    onChange?.(null);
  };

  return (
    <div data-testid={testId ? `${testId}-wrapper` : undefined}>
      <div className="inline-flex rounded-md border border-gray-200 overflow-hidden mb-2">
        <button
          type="button"
          onClick={() => { setMode("draw"); clear(); }}
          data-testid={testId ? `${testId}-mode-draw` : undefined}
          className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 ${mode === "draw" ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
        >
          <PenTool size={12} /> Draw
        </button>
        <button
          type="button"
          onClick={() => { setMode("type"); clear(); }}
          data-testid={testId ? `${testId}-mode-type` : undefined}
          className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 border-l border-gray-200 ${mode === "type" ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
        >
          <Type size={12} /> Type
        </button>
      </div>

      {mode === "draw" ? (
        <canvas
          ref={canvasRef}
          data-testid={testId}
          className="signature-pad-canvas"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
      ) : (
        <div className="space-y-2">
          <input
            type="text"
            value={typedName}
            onChange={(e) => renderTypedSignature(e.target.value)}
            placeholder="Type your full legal name"
            data-testid={testId ? `${testId}-type-input` : undefined}
            className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black/20"
          />
          {typedName && (
            <div
              className="signature-pad-canvas grid place-items-center"
              data-testid={testId ? `${testId}-type-preview` : undefined}
            >
              <span
                className="text-[#0a0a0a]"
                style={{
                  fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive',
                  fontStyle: "italic",
                  fontSize: typedName.length > 18 ? 38 : 56,
                  lineHeight: 1,
                }}
              >
                {typedName}
              </span>
            </div>
          )}
          {!typedName && (
            <div className="signature-pad-canvas grid place-items-center text-xs text-gray-400">
              Your typed signature will appear here
            </div>
          )}
          <p className="text-[11px] text-gray-500">
            By typing your name above you agree this constitutes your legal electronic signature under the E-SIGN Act and UETA.
          </p>
        </div>
      )}

      <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
        <span>{hasInk ? (mode === "type" ? "Signed (typed)" : "Signed") : (mode === "type" ? "Type to sign" : "Sign above")}</span>
        <button
          type="button"
          onClick={clear}
          className="text-gray-600 hover:text-black underline-offset-2 hover:underline inline-flex items-center gap-1"
          data-testid={`${testId}-clear`}
        >
          <Eraser size={11} /> Clear
        </button>
      </div>
    </div>
  );
}
